/**
 * ScreeningConsent — public page at /screening/consent/:token where a
 * tenant supplies their SIN, DOB, address and explicit PIPEDA consent.
 *
 * The page reads token state via GET /api/screening/consent/{token} and
 * submits via POST /api/screening/consent/{token}/submit. No SIN is ever
 * persisted in plain text on our side — the backend hashes it on receipt
 * and stores only the hash + last 3 digits for audit.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];

function formatSin(raw) {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

export default function ScreeningConsent() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", info: null });
  const [form, setForm] = useState({
    sin: "", dob: "", address_line1: "", city: "",
    province: "ON", postal_code: "", consent: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/screening/consent/${encodeURIComponent(token)}`);
        const d = await r.json();
        if (!alive) return;
        if (!r.ok) {
          setState({ loading: false, error: d?.detail || `HTTP ${r.status}`, info: null });
        } else {
          setState({ loading: false, error: "", info: d });
        }
      } catch (e) {
        if (alive) setState({ loading: false, error: String(e.message || e), info: null });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  async function submit(e) {
    e?.preventDefault?.();
    setErr("");
    if (!form.consent) { setErr("You must tick the consent box to proceed."); return; }
    const sinDigits = form.sin.replace(/\D/g, "");
    if (sinDigits.length !== 9) { setErr("SIN must contain 9 digits."); return; }
    if (!form.dob) { setErr("Date of birth is required."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/screening/consent/${encodeURIComponent(token)}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sin: sinDigits, dob: form.dob,
          address_line1: form.address_line1.trim(),
          city: form.city.trim(),
          province: form.province,
          postal_code: form.postal_code.trim().toUpperCase(),
          consent: true,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setDone(d);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white min-h-screen" data-testid="screening-consent-page">
      <PublicHeader light={true}/>
      <main className="max-w-2xl mx-auto px-5 md:px-8 py-10">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2">
          <ShieldCheck className="w-3 h-3"/> Secure screening
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tighter">Complete your rental screening</h1>
        <p className="text-sm text-slate-600 mt-2">Your information is encrypted in transit and your SIN is never stored in plain text on our servers.</p>

        {state.loading && (
          <div className="mt-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 inline"/></div>
        )}

        {!state.loading && state.error && (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900" data-testid="consent-error">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-none mt-0.5"/>
              <div>
                <div className="font-bold">We couldn't load this screening request.</div>
                <p className="mt-1">{state.error}</p>
                <p className="mt-2 text-xs">If you think this is a mistake, ask the landlord to send you a fresh link.</p>
              </div>
            </div>
          </div>
        )}

        {!state.loading && !state.error && state.info && !done && (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="consent-form">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-xs text-slate-500">For</div>
              <div className="font-display text-lg font-bold text-slate-900">{state.info.first_name}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">SIN *</label>
                <input
                  required
                  inputMode="numeric"
                  value={formatSin(form.sin)}
                  onChange={(e) => setForm({ ...form, sin: e.target.value })}
                  data-testid="consent-sin"
                  placeholder="123 456 789"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500 font-mono"
                />
                <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1"><Lock className="w-3 h-3"/> Encrypted in transit. Hashed at rest.</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Date of birth *</label>
                <input required type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  data-testid="consent-dob"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Current address *</label>
              <input required value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                data-testid="consent-address"
                placeholder="123 Main St, Apt 4B"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">City *</label>
                <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="consent-city"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Province *</label>
                <select required value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}
                  data-testid="consent-province"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Postal *</label>
                <input required value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  data-testid="consent-postal"
                  placeholder="A1A 1A1"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500 uppercase"/>
              </div>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.consent}
                  data-testid="consent-checkbox"
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-emerald-600"/>
                <span className="text-xs text-emerald-900 leading-relaxed">
                  {state.info.consent_text}
                </span>
              </label>
            </div>

            {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="consent-error-msg">{err}</div>}

            <button type="submit" disabled={busy} data-testid="consent-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Submitting…</> : <>Submit and run screening</>}
            </button>

            <p className="text-[10px] text-slate-500">By submitting, you authorize the credit pull described above. You can withdraw consent at any time before this page is submitted.</p>
          </form>
        )}

        {done && (
          <div className="mt-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="consent-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2"/>
            <div className="font-display text-xl font-extrabold text-slate-900">All done.</div>
            <p className="text-sm text-slate-600 mt-1">
              Your information has been submitted and the report has been delivered to the landlord. Status: <b>{done.status}</b>{done.sandbox ? " (sandbox)" : ""}.
            </p>
            <Link to="/" className="inline-block mt-4 text-xs font-bold text-emerald-700 underline">Back to Central Rentals</Link>
          </div>
        )}
      </main>
    </div>
  );
}
